import {ref, computed, watch} from 'vue'
import {defineStore} from 'pinia'
import analytics from 'shared/utils/analytics'
import sortArrayByObjectProperty from 'shared/utils/sortArrayByObjectProperty'
import workspaceSelectorStorage from 'shared/utils/workspaceSelectorStorage'
import collectionSelectorStorage from 'shared/utils/collectionSelectorStorage'
import delay from 'shared/utils/delay'
import messageExtension from 'shared/utils/messageExtension'
import {useRoute} from 'vue-router'
import {useWorkspaceSubscriptionStore} from 'shared/stores/workspaceSubscription'
import auth from 'shared/plugins/auth'
import {BROWSERSTACK_BUG_CAPTURE_URL} from 'shared/utils/browserstackUrls'
import Cookies from 'js-cookie'
import {FEATURE_CODES} from 'shared/constants/featureCodes'

export const useStore = defineStore('store', () => {
	const route = useRoute()
	const workspaceSubscription = useWorkspaceSubscriptionStore()

	// State set from `appDataLoader.js` (from GQL queries)
	const queries = ref({})

	const user = ref(undefined)

	const userWorkspaces = ref(undefined)

	const currentWorkspace = ref(undefined)

	// Other state
	const lastSelectedWorkspaceId = ref(null)

	const lastSelectedCollectionId = ref(null)

	const primaryWorkspaceGravatarColor = ref(true)

	const extensionMetadata = ref(null)

	const workspacesUserCanJoin = ref([])

	const theme = ref('dark')

	const isExtension = import.meta.env.VUE_APP_TARGET === 'extension'

	// LOADERS
	const loadingUser = computed(() => {
		return queries.value.user?.loading || user.value === undefined
	})

	const loadingUserWorkspaces = computed(() => {
		return queries.value.userWorkspaces?.loading && !Array.isArray(userWorkspaces.value)
	})

	const loadingCurrentWorkspace = computed(() => {
		return queries.value.currentWorkspace?.loading && !currentWorkspace.value
	})

	// USER
	const isBrowserstackUser = computed(() => {
		if (!user.value) return undefined
		return user.value?.id?.startsWith('oauth2|Browserstack')
	})

	const hasUserGivenConsent = computed(() => {
		if (isBrowserstackUser.value) return true
		return Boolean(user.value && user.value.consentedTermsOfServiceAt && user.value.consentedPrivacyPolicyAt)
	})

	const hasUserCompletedSignup = computed(() => {
		if (!user.value?.id) return true
		if (isBrowserstackUser.value) return true
		return Boolean(hasUserGivenConsent.value && user.value.jobRole && userWorkspaces.value?.length)
	})

	// USER WORKSPACES
	const workspaces = computed(() => userWorkspaces.value || [])

	// workspaces created before the Browserstack integration
	const legacyWorkspaces = computed(() => workspaces.value.filter(({workspace}) => !workspace.browserstackId))

	// CURRENT WORKSPACE
	const sessionRef = ref() // filled by the session store, used only for internal computations within this store

	const workspace = computed(() => {
		const isUserSessionWorkspaceMember = Boolean(
			workspaces.value.some(userWorkspace => userWorkspace.workspaceId === sessionRef.value?.workspaceId)
		)
		if (sessionRef.value?.id && !isUserSessionWorkspaceMember && !isExtension) {
			return {} // session loaded, but user is not a member
		}
		if (route?.name === 'workspaces-create') return {}
		const workspaceId = route?.params.workspaceId || lastSelectedWorkspaceId.value
		// if current workspace is loaded and IDs match, return it
		if (currentWorkspace.value?.id === workspaceId) {
			return currentWorkspace.value
		}
		const userWorkspace = workspaces.value.find(userWorkspace => userWorkspace.workspaceId === workspaceId)
		return userWorkspace?.workspace || {}
	})

	const workspaceUserSetting = computed(() => workspace.value.workspaceUserSettings?.[0] || {})

	const isBrowserstackWorkspace = computed(() => Boolean(workspace.value.browserstackId))

	// CURRENT WORKSPACE COLLECTIONS
	const workspaceCollections = computed(() => {
		const userWorkspace = workspaces.value.find(({workspaceId}) => workspaceId === workspace.value.id)
		const collections = userWorkspace?.workspace?.collections || []
		return collections.sort((a, b) => a.workspaceCollectionUsers[0].order - b.workspaceCollectionUsers[0].order)
	})

	const workspaceCollectionNames = computed(() => workspaceCollections.value.map(collection => collection.name))

	const workspaceUnarchivedCollections = computed(() => workspaceCollections.value.filter(({archived}) => !archived))

	const workspaceArchivedCollections = computed(() => workspaceCollections.value.filter(({archived}) => archived))

	// CURRENT COLLECTION
	const collection = computed(() => {
		if (!workspaceCollections.value.length) return {}
		const collectionId = route?.params.collectionId || lastSelectedCollectionId.value
		let selectedCollection = workspaceCollections.value.find(collection => collection.id === collectionId)
		// In extension, archived collections can't be accessed; in the web app that's possible
		if (isExtension && selectedCollection?.archived) selectedCollection = null
		return selectedCollection || selectUsersDefaultCollection() || {}
	})

	// CURRENT WORKSPACE USERS
	const users = computed(() => {
		if (!workspace.value.workspaceUsers) return []
		const users = workspace.value.workspaceUsers.map(workspaceUser => ({
			...workspaceUser.user,
			role: workspaceUser.role,
			lastActiveAt: workspaceUser.lastActiveAt,
		}))
		return sortArrayByObjectProperty(users, 'email')
	})

	const memberAndAdminUsersCount = computed(
		() => users.value.filter(entry => ['full-member', 'admin'].includes(entry.role)).length
	)

	const workspaceUserRole = computed(() => {
		const workspaceUser = workspaces.value.find(({workspaceId}) => workspaceId === workspace.value.id)
		return workspaceUser?.role
	})

	const isUserWorkspaceMember = computed(() => Boolean(workspaceUserRole.value))

	const hasUserBugReporterRole = computed(() => workspaceUserRole.value === 'bug-reporter')

	const hasUserProductViewerRole = computed(() => workspaceUserRole.value === 'viewer')

	const hasUserAdminRole = computed(() => workspaceUserRole.value === 'admin')

	const hasUserMemberRole = computed(() => workspaceUserRole.value === 'full-member')

	const hasUserMemberOrAdminRole = computed(() => hasUserMemberRole.value || hasUserAdminRole.value)

	const invites = computed(() => workspace.value.invites || [])

	const isUserAllowedToCreateWorkspace = computed(() => {
		const freeWorkspaces = workspaces.value.filter(({workspace}) => workspace.plan?.[0]?.type === 'free') || []
		const trialWorkspaces = workspaces.value.filter(({workspace}) => workspace.activePlanTrial.length) || []
		return freeWorkspaces.length + trialWorkspaces.length < 2
	})

	const isSessionLabelEnabled = computed(() => {
		// Use workspace setting if known. Otherwise show label.
		// --> Show session label in public sessions, where workspace data and setting are undefined.
		return workspace.value.sessionLabelEnabled != undefined ? workspace.value.sessionLabelEnabled : true
	})

	const planFeatures = computed(() => {
		// Fetch the plan-features from the userWorkspaces query
		const planFeatures = userWorkspaces.value?.[0]?.workspace?.plan?.[0]?.planFeatures
		// Return the plan-features or an empty array if not found
		return planFeatures || []
	})

	// INTEGRATIONS
	const isGithubIntegrationEnabled = computed(
		() => workspaceSubscription.isIntegrationAllowed && workspace.value.githubInstallationState === 'installed'
	)

	const isJiraIntegrationEnabled = computed(
		() => workspaceSubscription.isIntegrationAllowed && workspaceUserSetting.value.isJiraConnected
	)

	const isZapierIntegrationEnabled = computed({
		get: function () {
			return workspaceSubscription.isIntegrationAllowed && workspaceUserSetting.value.isZapierConnected
		},
		set: function (isConnected) {
			workspaceUserSetting.value = {
				...workspaceUserSetting.value,
				isZapierConnected: isConnected,
			}
		},
	})

	const isAzureIntegrationEnabled = computed(
		() => workspaceSubscription.isIntegrationAllowed && workspaceUserSetting.value.isAzureConnected
	)

	const isLinearIntegrationEnabled = computed(() =>
		Boolean(workspaceSubscription.isIntegrationAllowed && workspace.value.workspaceUserSettingsLinear?.isConnected)
	)

	const isTrelloIntegrationEnabled = computed(() =>
		Boolean(workspaceSubscription.isIntegrationAllowed && workspaceUserSetting.value.showShareInTrelloButton)
	)

	const isEmailManualIntegrationEnabled = computed(() =>
		Boolean(workspaceSubscription.isIntegrationAllowed && workspaceUserSetting.value.showShareInEmailButton)
	)

	// WATCHERS
	watch(
		() => workspaces.value.length,
		length => {
			if (!length) return

			if (lastSelectedWorkspaceId.value && workspace.value.id === lastSelectedWorkspaceId.value) return
			lastSelectedWorkspaceId.value = workspaceSelectorStorage.getLastSelectedWorkspaceId(auth.userId.value)
			// select workspace if there's none and the user with the user's workspaces have been loaded
			if (!lastSelectedWorkspaceId.value) return selectUsersDefaultWorkspace()
			// if selected workspace is not a part of the user's loaded workspaces reset the selection
			// this could happen if the user has been removed from the workspace the user last selected
			if (!isUserWorkspaceMember.value) return selectUsersDefaultWorkspace()
		}
	)

	watch(
		() => workspace.value,
		workspace => {
			if (!workspace?.id || !isUserWorkspaceMember.value) return
			lastSelectedWorkspaceId.value = workspace.id
			workspaceSelectorStorage.setLastSelectedWorkspaceId(workspace.id, auth.userId.value)

			let workspaceTraits = {fullMemberLimit: workspace.fullMemberLimit}
			const plan = workspace.plan?.[0]
			if (plan.id) {
				workspaceTraits = {
					...workspaceTraits,
					planId: plan.id,
					planName: plan.name,
					planIsPerSeat: plan.perSeat,
					planBillingFrequency: plan.billingFrequency,
				}
			}
			const activePlanTrial = workspace.activePlanTrial?.[0]
			if (activePlanTrial?.planId) {
				workspaceTraits.trialPlanId = activePlanTrial.planId
				workspaceTraits.trialDaysLeft = activePlanTrial.daysLeft
			}
			if (workspace.workspaceUsers?.length) workspaceTraits.memberCount = workspace.workspaceUsers.length
			if (workspace.invites?.length) workspaceTraits.memberPendingInviteCount = workspace.invites.length
			if (Object.keys(workspaceTraits).length) {
				analytics.group(workspace.browserstackId || workspace.id, workspaceTraits)
			}
		}
	)

	watch(
		() => collection.value,
		collection => {
			if (!collection?.id || !isUserWorkspaceMember.value) return
			lastSelectedCollectionId.value = collection.id
			collectionSelectorStorage.setLastSelectedCollectionId(collection.id, workspace.value.id)
		}
	)

	watch(
		() => auth.isAuthenticated.value,
		isAuthenticated => {
			if (!isAuthenticated) clearUserData()
		}
	)

	watch(
		() => user.value?.id,
		async userId => {
			if (!userId) return

			if (user.value.browserstackUserId) analytics.identify(user.value.browserstackUserId)

			const payload = route?.name !== 'logout' && {
				userInformation: {user: user.value, legacyUser: !user.value.browserstackUserId},
			}
			await messageExtension('setUserInformation', payload)

			// NOTE: this cookie is to demark users prior to shipping multiple auth flows, to know which users can be directed directly to the Auth0 screens.
			if (user.value.browserstackUserId) {
				Cookies.remove('beb-user')
			} else {
				Cookies.set('beb-user', new Date().toISOString(), {expires: 365})
			}
		}
	)

	// METHODS
	const hasPlanFeature = featureName => {
		// If planFeatures list is empty return true
		if (planFeatures.value.length === 0) return true
		return planFeatures.value.includes(featureName)
	}

	async function getIsUserWorkspaceMember(workspaceId) {
		// Whether a user is workspace member can only be determined once workspace has finished loading.
		while (loadingUserWorkspaces.value === true) {
			await delay(50)
		}
		return Boolean(workspaces.value.some(userWorkspace => userWorkspace.workspaceId === workspaceId))
	}

	function getIsUserMemberOrAdminOfWorkspace(workspaceId) {
		const workspaceUser = workspaces.value.find(userWorkspace => userWorkspace.workspaceId === workspaceId)
		const role = workspaceUser?.role
		return ['full-member', 'admin'].includes(role)
	}

	function selectUsersDefaultWorkspace() {
		if (!workspaces.value.length) return {}
		if (user.value?.browserstackUserId) {
			const workspaceWithActiveSubscription = workspaces.value.find(
				({workspace}) =>
					workspace.subscription.length !== 0 &&
					workspace.subscription[0].status === 'active' &&
					workspace.browserstackId != null
			)
			const legacyWorkspaceWithActiveSubscription = workspaces.value.find(
				({workspace}) =>
					workspace.subscription.length !== 0 &&
					workspace.subscription[0].status === 'active' &&
					workspace.browserstackId == null
			)
			const workspaceWithActivePlanTrial = workspaces.value.find(
				({workspace}) =>
					workspace.activePlanTrial.length != 0 &&
					workspace.activePlanTrial[0].status === 'active' &&
					workspace.browserstackId != null &&
					workspace.activePlanTrial[0].daysLeft >= 0
			)
			const legacyWorkspaceWithActivePlanTrial = workspaces.value.find(
				({workspace}) =>
					workspace.activePlanTrial.length !== 0 &&
					workspace.activePlanTrial[0].status === 'active' &&
					workspace.browserstackId == null &&
					workspace.activePlanTrial[0].daysLeft >= 0
			)
			const defaultWorkspaceId =
				workspaceWithActiveSubscription?.workspace.id ||
				workspaceWithActivePlanTrial?.workspace.id ||
				legacyWorkspaceWithActiveSubscription?.workspace.id ||
				legacyWorkspaceWithActivePlanTrial?.workspace.id

			const defaultWorkspace = workspaces.value.find(({workspace}) => workspace.id === defaultWorkspaceId)
			if (defaultWorkspace) {
				lastSelectedWorkspaceId.value = defaultWorkspace.workspace.id
				return defaultWorkspace
			}
		}
		const workspaceCreatedByUserOnSignup = workspaces.value.find(
			({workspace}) => workspace.createdBy === auth.userId.value && workspace.createdOnSignup
		)
		if (workspaceCreatedByUserOnSignup) {
			lastSelectedWorkspaceId.value = workspaceCreatedByUserOnSignup.workspaceId
			return workspaceCreatedByUserOnSignup
		}

		lastSelectedWorkspaceId.value = workspaces.value[0]?.workspaceId
		return workspaces.value[0]
	}

	function selectUsersDefaultCollection() {
		if (!workspaceCollections.value?.length) return

		let defaultCollection

		// Select the last used folder of the workspace (if known)
		if (!collection.value.id && !lastSelectedCollectionId.value) {
			const lastSelectedCollectionId = collectionSelectorStorage.getLastSelectedCollectionId(workspace.value.id)
			defaultCollection = workspaceUnarchivedCollections.value.find(
				collection => collection.id === lastSelectedCollectionId
			)
		}

		if (!defaultCollection) {
			// in the app, if user has only 2 folders, select the closed one so they can see the demo sessions
			if (isExtension && workspaceUnarchivedCollections.value.length <= 2) {
				if (!hasPlanFeature(FEATURE_CODES.PRIVATE_FOLDERS)) {
					defaultCollection = workspaceUnarchivedCollections.value.find(
						collection => collection.accessPermission === 'open'
					)
				} else {
					defaultCollection = workspaceUnarchivedCollections.value.find(
						collection => collection.accessPermission === 'closed'
					)
				}
			} else if (!isExtension) {
				// otherwise, we prefer public folders, as Bird is a team-first tool
				defaultCollection = workspaceUnarchivedCollections.value.find(
					collection => collection.accessPermission === 'open'
				)
			}
		}
		// Fall back to the 1st non-archived folder
		if (!defaultCollection) {
			if (!hasPlanFeature(FEATURE_CODES.PRIVATE_FOLDERS)) {
				defaultCollection = workspaceUnarchivedCollections.value.find(
					collection => collection.accessPermission === 'open'
				)
				if (!defaultCollection) {
					defaultCollection = workspaceUnarchivedCollections.value[0]
				}
			} else {
				defaultCollection = workspaceUnarchivedCollections.value[0]
			}
		}
		lastSelectedCollectionId.value = defaultCollection.id
		return defaultCollection
	}

	function clearUserData() {
		user.value = null
		userWorkspaces.value = null
		currentWorkspace.value = null
		lastSelectedWorkspaceId.value = null
		lastSelectedCollectionId.value = null
	}

	const productName = 'Bug Capture'

	const productIcon = 'mono/bug-capture'

	const productUrl = BROWSERSTACK_BUG_CAPTURE_URL

	return {
		queries,
		user,
		isBrowserstackUser,
		userWorkspaces,
		legacyWorkspaces,
		currentWorkspace,
		lastSelectedWorkspaceId,
		lastSelectedCollectionId,
		primaryWorkspaceGravatarColor,
		extensionMetadata,
		workspacesUserCanJoin,
		theme,
		isExtension,
		sessionRef,
		//computed
		loadingUser,
		loadingUserWorkspaces,
		loadingCurrentWorkspace,
		hasUserGivenConsent,
		hasUserCompletedSignup,
		workspaces,
		workspace,
		isBrowserstackWorkspace,
		workspaceUserSetting,
		workspaceCollections,
		workspaceCollectionNames,
		workspaceUnarchivedCollections,
		workspaceArchivedCollections,
		collection,
		users,
		memberAndAdminUsersCount,
		workspaceUserRole,
		isUserWorkspaceMember,
		hasUserBugReporterRole,
		hasUserProductViewerRole,
		hasUserAdminRole,
		hasUserMemberRole,
		hasUserMemberOrAdminRole,
		invites,
		isUserAllowedToCreateWorkspace,
		isGithubIntegrationEnabled,
		isSessionLabelEnabled,
		isJiraIntegrationEnabled,
		isZapierIntegrationEnabled,
		isAzureIntegrationEnabled,
		isLinearIntegrationEnabled,
		isTrelloIntegrationEnabled,
		isEmailManualIntegrationEnabled,
		productName,
		productIcon,
		productUrl,
		//methods
		getIsUserWorkspaceMember,
		getIsUserMemberOrAdminOfWorkspace,
		selectUsersDefaultWorkspace,
		selectUsersDefaultCollection,
		clearUserData,
		planFeatures,
		hasPlanFeature,
	}
})
